'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _extends = require('@babel/runtime/helpers/extends');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var _objectWithoutProperties = require('@babel/runtime/helpers/objectWithoutProperties');
var isEmpty = require('lodash/isEmpty.js');
var isUndefined = require('lodash/isUndefined.js');
var countBy = require('lodash/countBy.js');
var last = require('lodash/last.js');
var isNaN = require('lodash/isNaN.js');
var isNull = require('lodash/isNull.js');
var react = require('@chakra-ui/react');
var React = require('react');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var _objectWithoutProperties__default = /*#__PURE__*/_interopDefaultLegacy(_objectWithoutProperties);
var isEmpty__default = /*#__PURE__*/_interopDefaultLegacy(isEmpty);
var isUndefined__default = /*#__PURE__*/_interopDefaultLegacy(isUndefined);
var countBy__default = /*#__PURE__*/_interopDefaultLegacy(countBy);
var last__default = /*#__PURE__*/_interopDefaultLegacy(last);
var isNaN__default = /*#__PURE__*/_interopDefaultLegacy(isNaN);
var isNull__default = /*#__PURE__*/_interopDefaultLegacy(isNull);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var _excluded = ["format", "value", "defaultValue", "onChange", "onBlur", "onFocus"];

/**
 * Provides format specific formation instructions.
 */
var FORMAT = {
  number: {
    inputmode: 'numeric'
  },
  size: {
    // scheme: xx,yy where xx length <=20 and not start with 0 and ,yy is optional
    // Note: 0,yy is not allowed as it makes no sense in our case
    pattern: /^[1-9][0-9]{0,19}(\.[0-9]{1,2})?$/,
    inputmode: 'decimal'
  },
  price: {
    inputmode: 'decimal'
  },
  year: {
    // scheme: xxxx where everything above year 0 is allowed.
    // Note: No starting Zero.
    pattern: /^[1-9][0-9]{0,3}$/,
    inputmode: 'numeric'
  }
};

/**
 * Provides regex functions.
 */
var REGEX = {
  allowedChars: /[0-9,.-]/g,
  allDots: /\./g,
  allDotsAndCommas: /,|\./g,
  firstDotOrComma: /,|\./,
  startingDotOrCommaIgnoreNegation: /^-?(,|\.)/,
  startingNegation: /^-/,
  startingZeroBeforeNumber: /^0[0-9]/,
  noNumbers: /[^0-9]+/,
  firstComma: /,/,
  endingDotOrComma: /,|\.$/,
  allUpToThreeNumbers: /[0-9]{1,3}/g
};

/**
 * Customized `onChange()` event.
 */

var InputFormatted = react.forwardRef(function (_ref, ref) {
  var _ref$format = _ref.format,
    format = _ref$format === void 0 ? 'number' : _ref$format,
    value = _ref.value,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    rest = _objectWithoutProperties__default["default"](_ref, _excluded);
  /**
   * Blocks all chars which are not valid for given input type.
   * Also convert to string for following actions.
   */
  var filterAllowedChars = function filterAllowedChars(valueRaw) {
    var stringValue = valueRaw.toString();
    // Removes all chars which are not one of the following
    return (stringValue.match(REGEX.allowedChars) || []).join('');
  };

  /**
   * Some save QoL formatting which should highly improve the UX.
   */
  var basicFormat = function basicFormat(valueRaw) {
    var valueEdged = valueRaw;
    // As Price has dots as separator we need to intercept here
    // to ensure they are not interpreted as decimal separator.
    // e.g. 3.000,4 (visual) -> 3000,4 (visual)
    if (format === 'price') valueEdged = valueEdged.replace(REGEX.allDots, '');

    // Adds leading 0 if user starts with separator (and has no more of them)
    // e.g. ,3 -> 0.3; .3 -> 0.3
    if (valueEdged.match(REGEX.startingDotOrCommaIgnoreNegation)) {
      var separators = valueEdged.match(REGEX.allDotsAndCommas);
      var amountSeparators = separators.length;
      if (amountSeparators === 1) return valueEdged.replace(REGEX.firstDotOrComma, format === 'price' ? '0,' : '0.');
    }
    return valueEdged;
  };

  /**
   * Check if the value has a valid format as number.
   */
  var checkIsNumber = function checkIsNumber(valueRaw) {
    if (isEmpty__default["default"](valueRaw)) return false;
    if (isUndefined__default["default"](valueRaw)) return false;
    if (isNaN__default["default"](valueRaw)) return false;
    if (isNull__default["default"](valueRaw)) return false;

    // Ignore starting negation as it is not relevant here
    var valueWithoutSign = valueRaw.replace(REGEX.startingNegation, '');

    // Invalidate numbers with leading 0 (except followed by separator)
    if (valueRaw.match(REGEX.startingZeroBeforeNumber)) return false;

    // Get Count of separators
    var separators = valueWithoutSign.match(REGEX.allDotsAndCommas);
    var countedSeparators = countBy__default["default"](separators, function (_char) {
      return _char;
    });
    // Get chunks splitted by separators
    var splitNumbers = valueWithoutSign.split(REGEX.firstDotOrComma);

    // Catch weird inputs like 11.11.11 | 11,11.11,11
    if (countedSeparators['.'] > 1 && countedSeparators[','] > 1) return false;
    // Check all chunks
    if (splitNumbers.length) {
      return splitNumbers.every(function (chunk, index) {
        var isFirst = index === 0;
        var isLast = splitNumbers.length === index + 1;

        // if a chunk is empty there were 2 following separators
        if (isEmpty__default["default"](chunk)) return false;
        // If a chunk contain different than number (e.g. '-')
        if (chunk.match(REGEX.noNumbers)) return false;

        // Only checks middle chunks
        if (splitNumbers.length > 2 && !isFirst && !isLast) {
          // middle chunks should have length of 3
          return chunk.length === 3;
        }
        return true;
      });
    }
    return true;
  };

  /**
   * Ensures correct "invalid" detection of given value as `0`, `NaN`, `null` etc. all act differently on checks.
   */
  var isValidNumber = function isValidNumber(valueToCheck) {
    if (valueToCheck === undefined || valueToCheck === null || isNaN__default["default"](valueToCheck) || valueToCheck === '') {
      return false;
    }
    return true;
  };

  /**
   * Adds a negation sign to number if needed.
   */
  var addSign = function addSign(number, negation) {
    return negation ? "-".concat(number) : number;
  };

  /**
   * Format number string to basic format.
   * Result should always be something like '123445' or '123445.34'
   */
  var convertToRawNumber = function convertToRawNumber(valueRaw) {
    var hasSign = valueRaw.startsWith('-');
    var valueWithoutSign = valueRaw.replace(REGEX.startingNegation, '');

    // Get separators and count the occurrences
    var separators = valueWithoutSign.match(REGEX.allDotsAndCommas);
    var countedSeparators = countBy__default["default"](separators, function (_char2) {
      return _char2;
    });
    var hasBothSeparators = Object.keys(countedSeparators).length === 2;
    var lastSeparator = last__default["default"](separators);
    var isLastSeparatorUnique = countedSeparators[lastSeparator] === 1;

    // Remove thousand separators
    // Like 12.345.678 12,345,678 -> 12345678
    if (!isLastSeparatorUnique && !hasBothSeparators) {
      return addSign(valueWithoutSign.replace(REGEX.allDotsAndCommas, ''), hasSign);
    }

    // Remove thousand separators but keep/transform decimal separator
    // 1.234.567.890,45 1,234,567,890.45 -> 1234567889.45
    if (isLastSeparatorUnique && hasBothSeparators) {
      return addSign(valueWithoutSign.replace(lastSeparator, '*').replace(REGEX.allDotsAndCommas, '').replace('*', '.'), hasSign);
    }

    // Convert decimal separator to dot
    // 1.2 1,2 -> 1.2
    if (isLastSeparatorUnique && !hasBothSeparators) {
      return addSign(valueWithoutSign.replace(REGEX.firstComma, '.'), hasSign);
    }
    return valueRaw;
  };

  /**
   * Transform value for visual output.
   */
  var formatValue = function formatValue(_ref2) {
    var valueRaw = _ref2.valueRaw,
      _ref2$isValid = _ref2.isValid,
      isValid = _ref2$isValid === void 0 ? true : _ref2$isValid,
      oldFormattedValue = _ref2.oldFormattedValue,
      oldValueRaw = _ref2.oldValueRaw;
    // Prevent breaking style if user enters a trailing comma
    // (Transition from valid integer to valid float)
    if (!isValid) {
      // Return old formatted value with added comma
      // if only the comma differs in change
      if (valueRaw.match(REGEX.endingDotOrComma) && oldValueRaw && oldValueRaw.length === valueRaw.length - 1 && oldValueRaw === valueRaw.replace(REGEX.endingDotOrComma, '')) {
        return "".concat(oldFormattedValue, ",");
      }
      return valueRaw;
    }
    var hasSign = (valueRaw + '').startsWith('-');
    var valueWithoutSign = valueRaw.replace(REGEX.startingNegation, '');
    var returnValue = valueWithoutSign.split('.');

    // Adds spaces for every 3 digits if number has more than 4 significant
    if (returnValue[0].length > 4) {
      returnValue[0] = returnValue[0].split('').reverse().join('').match(REGEX.allUpToThreeNumbers).map(function (reverseOrder) {
        return reverseOrder.split('').reverse().join('');
      }).reverse().join(format === 'price' ? '.' : ' ');
    }

    // Adds spaces for every 3 digits if decimal part has more than 4
    if (returnValue[1] && returnValue[1].length > 4) {
      returnValue[1] = returnValue[1].match(REGEX.allUpToThreeNumbers).join(' ');
    }

    // Combine significant part and decimal part by comma
    return addSign(returnValue.join(','), hasSign);
  };

  /**
   * Parse the value to number.
   */
  var parseValue = function parseValue(valueRaw) {
    // if is decimal parse to float
    if (valueRaw.includes('.')) return parseFloat(valueRaw);
    // Try parse to Integer
    var integer = parseInt(valueRaw, 10);
    // If NaN return undefined
    return isNaN__default["default"](integer) ? undefined : integer;
  };
  var _useState = React.useState(function () {
      var isValidNumberChecked = isValidNumber(value || defaultValue);
      return {
        raw: isValidNumberChecked ? value || defaultValue : null,
        formatted: formatValue({
          valueRaw: isValidNumberChecked ? (value || defaultValue).toString() : ''
        }),
        number: isValidNumberChecked ? value || defaultValue : null
      };
    }),
    _useState2 = _slicedToArray__default["default"](_useState, 2),
    data = _useState2[0],
    setData = _useState2[1];

  /**
   * Process the new value given by `value` and `onChange` AFTER the first initializing.
   */
  var processValue = function processValue(newValue) {
    // Block chars which are not related to number inputs
    var allowedValue = filterAllowedChars(newValue);

    // Do some special pre-formatting
    var unifiedValue = basicFormat(allowedValue);

    // Check if input is a valid number
    var isNumberValid = checkIsNumber(unifiedValue);

    // Check if invalid number is invalid because of a single trailing separator
    var hasNumberSingleTrailingSeperator = !isNumberValid && unifiedValue.match(REGEX.endingDotOrComma) && !unifiedValue.replace(REGEX.endingDotOrComma, '').match(REGEX.endingDotOrComma);

    // Transform number to processable base format
    var rawNumber = isNumberValid ? convertToRawNumber(unifiedValue) : unifiedValue;

    // Transform number for visual output
    var visualNumber = formatValue({
      valueRaw: rawNumber + '',
      isValid: isNumberValid,
      oldFormattedValue: data.formatted,
      oldValueRaw: data.raw
    });

    // Transform number to backend compatible output
    var dataNumber = function dataNumber() {
      if ((rawNumber + '').length === 0) return null;
      if (isNumberValid) return parseValue(rawNumber);
      if (hasNumberSingleTrailingSeperator) return +rawNumber.replace(REGEX.endingDotOrComma, '');
      return NaN;
    };
    var newData = {
      raw: rawNumber,
      formatted: visualNumber,
      number: dataNumber()
    };
    return newData;
  };

  /**
   * Listen to direct change of `value` prop and updates the visual data according to it.
   */
  React.useEffect(function () {
    if (isValidNumber(value) && value != data.number) {
      setData({
        raw: value,
        formatted: formatValue({
          valueRaw: value + ''
        }),
        number: value
      });
    }
  }, [value]);

  /**
   * Listen to `format" prop change and updates the visual data according to it.
   */
  React.useEffect(function () {
    // Only run formatting if there is something to format.
    if (isValidNumber(value)) {
      setData(function (oldData) {
        return {
          raw: oldData.raw,
          formatted: formatValue({
            valueRaw: oldData.raw + ''
          }),
          number: oldData.number
        };
      });
    }
  }, [format]);

  /**
   * Handles DOM events (onChange, onBlur, onFocus) and adds our custom data to it
   */
  var handleEvent = function handleEvent(event) {
    var newData = processValue(event.target.value);
    setData(newData);

    /*
     * Pass our custom data to the event.
     * A given `onChange()` should listen on `event.detail.number` instead of `event.target.value`.
     */
    // eslint-disable-next-line no-param-reassign
    event.detail = newData;
  };
  var handleChange = function handleChange(event) {
    handleEvent(event);
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };
  var handleBlur = function handleBlur(event) {
    handleEvent(event);
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };
  var handleFocus = function handleFocus(event) {
    handleEvent(event);
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  };
  return /*#__PURE__*/React__default["default"].createElement(react.Input, _extends__default["default"]({
    value: data.formatted,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus
    // Enable number keyboard on mobile devices
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
    ,

    inputMode: FORMAT[format].inputmode,
    ref: ref
  }, rest, {
    type: "text"
  }));
});

// This is used in `InputGroup`
InputFormatted.id = 'Input';
InputFormatted.displayName = 'InputFormatted';
exports.InputFormatted = InputFormatted;